// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { screenerApi } from '@advisor-app/store/api/ai/screener-api';
import type { MutualFundFilters } from '@shared/data-access/interfaces/api/ai/mutual-funds';
import { usePagination } from '@shared/ui-kit/components/grouped-table';
import { useDebounce } from '@shared/ui-kit/hooks';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { formatFilter } from '../utils/format-filters-payload';
import { buildMutualFundSearchPayload } from '../utils/build-mutual-fund-search-payload';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  resetMutualFundsFilters,
  selectMutualFundsFilters,
  setMutualFundsFilters,
} from '@advisor-app/store/slices/my-research';
import {
  isMutualFundsScreenerInitialState,
  mutualFundLocationsExcludingDefault,
} from '@advisor-app/store/slices/my-research/mutual-fund-screener-defaults';
import { useTranslation } from 'react-i18next';

const ALL_OPTION = { label: 'ALL', value: '' };

const DEFAULT_FILTERS_OPTIONS = {
  asset_class: [],
  asset_manager: [],
  category: [],
  currency: [],
  location: [],
  segment: [],
};

export const useSearchMutualFunds = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const pagination = usePagination();

  const reduxFilters = useAppSelector(selectMutualFundsFilters);

  const [localFilters, setLocalFilters] = useState<MutualFundFilters>(reduxFilters);

  const {
    data: filtersOptions = DEFAULT_FILTERS_OPTIONS,
    isFetching: isFetchingFilters,
  } = screenerApi.useGetMutualFundsFiltersQuery();

  const [textSearch, setTextSearch] = useState('');
  const debounceValue = useDebounce(textSearch, 300);
  const handleTextSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    void setTextSearch(event.target.value);

  const formattedFilters: MutualFundFilters = useMemo(
    () => ({
      ...reduxFilters,
      text_search: debounceValue,
      min_expenses_ratio: formatFilter('min_expenses_ratio', reduxFilters.ter),
      max_expenses_ratio: formatFilter('max_expenses_ratio', reduxFilters.ter),
      min_nav: formatFilter('min_nav', reduxFilters.aum),
      max_nav: formatFilter('max_nav', reduxFilters.aum),
      min_aum: formatFilter('min_aum', reduxFilters.aum),
      max_aum: formatFilter('max_aum', reduxFilters.aum),
    }),
    [reduxFilters, debounceValue],
  );

  const payload = useMemo(
    () => buildMutualFundSearchPayload(formattedFilters),
    [formattedFilters],
  );

  const skipSearchMutualFunds =
    isFetchingFilters ||
    filtersOptions.location.length === 0 ||
    (isMutualFundsScreenerInitialState(reduxFilters) &&
      filtersOptions.location.length > 0);

  useLayoutEffect(() => {
    if (!filtersOptions.location.length) {
      setLocalFilters(reduxFilters);
      return;
    }
    if (isMutualFundsScreenerInitialState(reduxFilters)) {
      const nextLocation = mutualFundLocationsExcludingDefault(
        filtersOptions.location,
      );
      const nextFilters = { ...reduxFilters, location: nextLocation };
      dispatch(setMutualFundsFilters(nextFilters));
      setLocalFilters(nextFilters);
      return;
    }
    setLocalFilters(reduxFilters);
  }, [dispatch, filtersOptions.location, reduxFilters]);

  const mutualFundsQuery = screenerApi.useSearchMutualFundsQuery(
    {
      payload,
      params: { assets_page: pagination.pageSize, page: pagination.pageIndex },
    },
    { skip: skipSearchMutualFunds },
  );

  const isFetchingMf = mutualFundsQuery?.isFetching ?? false;
  const mfResponse = mutualFundsQuery?.data;
  const mfError = mutualFundsQuery?.error;

  useEffect(() => {
    if (mfResponse?.pages) {
      pagination.onPaginationCountChange(mfResponse.pages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfResponse]);

  const setFilter = useCallback(
    <T extends keyof MutualFundFilters>(key: T, value: MutualFundFilters[T]) => {
      setLocalFilters(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const applyFilters = useCallback(() => {
    dispatch(setMutualFundsFilters(localFilters));
    pagination.onPaginationChange({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
  }, [dispatch, localFilters, pagination]);

  const resetFilters = useCallback(() => {
    dispatch(resetMutualFundsFilters());
  }, [dispatch]);

  const mutualFundFilters = useMemo(
    () =>
      [
        {
          key: 'location',
          label: t('research.market-location', { ns: 'research' }) as string,
          placeholder: t('research.select-market', { ns: 'research' }) as string,
          options: filtersOptions.location,
          onChange: (value: string[]) => setFilter('location', value),
          selected: localFilters.location,
          multiple: true,
        },
        {
          key: 'currency',
          label: t('common.currency', { ns: 'common' }) as string,
          placeholder: t('common.select-currency', { ns: 'common' }) as string,
          options: filtersOptions.currency,
          onChange: (value: string[]) => setFilter('currency', value),
          selected: localFilters.currency,
          multiple: true,
        },
        {
          key: 'asset_manager',
          label: t('glossary.assets-manager', { ns: 'glossary' }) as string,
          placeholder: t('research.select-asset-manager', {
            ns: 'research',
          }) as string,
          options: [...filtersOptions.asset_manager],
          onChange: (value: string[]) => setFilter('asset_manager', value),
          selected: localFilters.asset_manager,
          multiple: true,
          searchBar: true,
        },
        {
          key: 'aum',
          label: t('glossary.aum', { ns: 'glossary' }) as string,
          placeholder: t('glossary.mav', { ns: 'glossary' }) as string,
          options: [ALL_OPTION, '+300M', '100M - 300M', '50M - 100M', '0M - 50M'],
          onChange: (value: string) => setFilter('aum', value),
          selected: localFilters.aum,
        },
        {
          key: 'asset_class',
          label: t('glossary.asset-class', { ns: 'glossary' }) as string,
          placeholder: t('research.select-asset-class', {
            ns: 'research',
          }) as string,
          options: [...filtersOptions.asset_class],
          onChange: (value: string[]) => setFilter('asset_class', value),
          selected: localFilters.asset_class,
          multiple: true,
        },
        {
          key: 'segment',
          label: t('glossary.segment', { ns: 'glossary' }) as string,
          placeholder: t('research.select-segment', { ns: 'research' }) as string,
          options: [...filtersOptions.segment],
          onChange: (value: string[]) => setFilter('segment', value),
          selected: localFilters.segment,
          multiple: true,
          searchBar: true,
        },
        {
          key: 'category',
          label: t('research.category', { ns: 'research' }) as string,
          placeholder: t('research.select-category', { ns: 'research' }) as string,
          options: [...filtersOptions.category],
          onChange: (value: string[]) => setFilter('category', value),
          selected: localFilters.category,
          multiple: true,
          searchBar: true,
        },
        {
          key: 'ter',
          label: t('glossary.ter', { ns: 'glossary' }) as string,
          placeholder: 'TER',
          options: [
            ALL_OPTION,
            '0.0% - 0.2%',
            '0.2% - 0.5%',
            '0.5% - 1.0%',
            '1.0% - 1.5%',
            '+1.5%',
          ],
          onChange: (value: string) => setFilter('ter', value),
          selected: localFilters.ter,
        },
      ].map(filter => ({
        ...filter,
        onChange: (...value: Parameters<typeof filter.onChange>) => {
          filter.onChange(...(value as [string & string[]]));
        },
      })),
    [
      localFilters.asset_class,
      localFilters.asset_manager,
      localFilters.aum,
      localFilters.category,
      localFilters.currency,
      localFilters.location,
      localFilters.segment,
      localFilters.ter,
      filtersOptions.asset_class,
      filtersOptions.asset_manager,
      filtersOptions.category,
      filtersOptions.currency,
      filtersOptions.location,
      filtersOptions.segment,
      setFilter,
      t,
    ],
  );

  const isLoading = useMemo(
    () => [isFetchingFilters, isFetchingMf].some(Boolean),
    [isFetchingFilters, isFetchingMf],
  );

  return {
    filters: mutualFundFilters,
    areFiltersLoaded: !isFetchingFilters,
    error: mfError,
    isLoading,
    mutualFunds: mfResponse?.mfs ?? [],
    textSearch,
    handleTextSearch,
    pagination,
    resetFilters,
    applyFilters,
    localFilters,
  };
};
